import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CreateGiveaway = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    prize: '',
    endDate: '',
    scheduledPublishDate: '',
    specialThanksName: '',
    specialThanksMessage: ''
  });
  const [banner, setBanner] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [numberOfWinners, setNumberOfWinners] = useState(1);
  const [autoPayoutEnabled, setAutoPayoutEnabled] = useState(false);
  const [autoRollEnabled, setAutoRollEnabled] = useState(false);
  const [supporterBonus, setSupporterBonus] = useState(false);
  const [gameKeys, setGameKeys] = useState([{ position: 1, key: '' }]);
  const [rerollTimerHours, setRerollTimerHours] = useState(24);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Template management state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateSection, setShowTemplateSection] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const requirementTypes = [
    // Twitter/X
    { value: 'twitter_follow', label: '🐦 Follow on Twitter/X' },
    { value: 'twitter_like', label: '❤️ Like Twitter/X Post' },
    { value: 'twitter_retweet', label: '🔄 Retweet Twitter/X Post' },
    { value: 'twitter_comment', label: '💬 Comment on Twitter/X Post' },
    
    // Instagram
    { value: 'instagram_follow', label: '📸 Follow on Instagram' },
    { value: 'instagram_like', label: '❤️ Like Instagram Post' },
    { value: 'instagram_comment', label: '💬 Comment on Instagram Post' },
    
    // YouTube
    { value: 'youtube_subscribe', label: '▶️ Subscribe on YouTube' },
    { value: 'youtube_like', label: '👍 Like YouTube Video' },
    { value: 'youtube_comment', label: '💬 Comment on YouTube Video' },
    
    // Twitch
    { value: 'twitch_follow', label: '🎮 Follow on Twitch' },
    { value: 'twitch_subscribe', label: '⭐ Subscribe on Twitch' },
    
    // Kick
    { value: 'kick_follow', label: '🦵 Follow on Kick' },
    
    // TikTok
    { value: 'tiktok_follow', label: '🎵 Follow on TikTok' },
    { value: 'tiktok_like', label: '❤️ Like TikTok Video' },
    
    // Facebook
    { value: 'facebook_like', label: '👍 Like Facebook Page' },
    { value: 'facebook_share', label: '🔄 Share Facebook Post' },
    
    // Discord
    { value: 'discord_join', label: '💬 Join Discord Server' },
    
    // Reddit
    { value: 'reddit_join', label: '🔴 Join Subreddit' },
    { value: 'reddit_upvote', label: '⬆️ Upvote Reddit Post' },
    
    // Steam
    { value: 'steam_follow', label: '🎮 Follow on Steam' },
    { value: 'steam_wishlist', label: '❤️ Wishlist on Steam' },
    
    // Other
    { value: 'custom_url', label: '🔗 Visit Custom URL' }
  ];

  // Load templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/giveaway-templates', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTemplates(response.data.templates || []);
      } catch (error) {
        console.error('Error loading templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, []);

  // Handle template selection
  const handleTemplateSelect = async (templateId) => {
    if (!templateId) {
      setSelectedTemplate(null);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/giveaway-templates/${templateId}/use`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const template = response.data.template;
      setSelectedTemplate(template);

      // Load template data into form
      const templateData = template.templateData;
      setFormData({
        ...formData,
        title: templateData.title || '',
        description: templateData.description || '',
        prize: templateData.prize || '',
        specialThanksName: templateData.specialThanks || '',
        specialThanksMessage: ''
      });
      setRequirements(templateData.requirements || []);
      setNumberOfWinners(templateData.numberOfWinners || 1);
      setAutoPayoutEnabled(templateData.autoPayoutEnabled || false);
      setAutoRollEnabled(templateData.autoRollEnabled || false);
      setRerollTimerHours(templateData.rerollTimerHours || 24);
      setSupporterBonus(templateData.supporterBonus || false);
    } catch (error) {
      console.error('Error loading template:', error);
      setError('Failed to load template');
    }
  };

  // Save current form as template
  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      setError('Please enter a template name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/giveaway-templates', {
        name: templateName,
        templateData: {
          title: formData.title,
          description: formData.description,
          prize: formData.prize,
          specialThanks: formData.specialThanksName,
          requirements,
          numberOfWinners,
          autoPayoutEnabled,
          autoRollEnabled,
          rerollTimerHours,
          supporterBonus
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTemplateName('');
      setSaveAsTemplate(false);
      alert('Template saved successfully!');
      
      // Refresh templates list
      const response = await axios.get('/api/giveaway-templates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error saving template:', error);
      setError(error.response?.data?.message || 'Failed to save template');
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Banner image must be less than 10MB');
        return;
      }
      setBanner(file);
      setBannerPreview(URL.createObjectURL(file));
      setError('');
    }
  };

  // Helper function to sanitize and normalize URLs
  const sanitizeUrl = (url) => {
    if (!url || !url.trim()) return '';
    
    let cleanUrl = url.trim();
    
    // Convert HTTP:// to https:// (case insensitive)
    cleanUrl = cleanUrl.replace(/^HTTP:\/\//i, 'https://');
    
    // If URL doesn't have a protocol, add https://
    if (!cleanUrl.match(/^https?:\/\//i)) {
      cleanUrl = 'https://' + cleanUrl;
    }
    
    // Normalize the URL to lowercase (except for case-sensitive path/query parts)
    try {
      const urlObj = new URL(cleanUrl);
      // Convert protocol and hostname to lowercase
      urlObj.protocol = urlObj.protocol.toLowerCase();
      urlObj.hostname = urlObj.hostname.toLowerCase();
      cleanUrl = urlObj.toString();
    } catch (e) {
      // If URL is invalid, just return the cleaned version
      console.warn('Invalid URL format:', cleanUrl);
    }
    
    return cleanUrl;
  };

  // Helper function to generate description based on type and input
  const generateDescription = (type, input) => {
    if (!input) return '';
    
    const cleanInput = input.trim();
    
    switch (type) {
      case 'twitter_follow':
        return `Follow ${cleanInput.startsWith('@') ? cleanInput : '@' + cleanInput} on Twitter/X`;
      case 'twitter_like':
        return `Like this post on Twitter/X`;
      case 'twitter_retweet':
        return `Retweet this post on Twitter/X`;
      case 'twitter_comment':
        return `Comment on this post on Twitter/X`;
      case 'instagram_follow':
        return `Follow ${cleanInput.startsWith('@') ? cleanInput : '@' + cleanInput} on Instagram`;
      case 'instagram_like':
        return `Like this post on Instagram`;
      case 'instagram_comment':
        return `Comment on this post on Instagram`;
      case 'youtube_subscribe':
        return `Subscribe to ${cleanInput} on YouTube`;
      case 'youtube_like':
        return `Like this video on YouTube`;
      case 'youtube_comment':
        return `Comment on this video on YouTube`;
      case 'twitch_follow':
        return `Follow ${cleanInput} on Twitch`;
      case 'twitch_subscribe':
        return `Subscribe to ${cleanInput} on Twitch`;
      case 'kick_follow':
        return `Follow ${cleanInput} on Kick`;
      case 'tiktok_follow':
        return `Follow ${cleanInput.startsWith('@') ? cleanInput : '@' + cleanInput} on TikTok`;
      case 'tiktok_like':
        return `Like this video on TikTok`;
      case 'facebook_like':
        return `Like ${cleanInput} on Facebook`;
      case 'facebook_share':
        return `Share this post on Facebook`;
      case 'discord_join':
        return `Join ${cleanInput} Discord Server`;
      case 'reddit_join':
        return `Join ${cleanInput.startsWith('r/') ? cleanInput : 'r/' + cleanInput} subreddit`;
      case 'reddit_upvote':
        return `Upvote this post on Reddit`;
      case 'steam_follow':
        return `Follow ${cleanInput} on Steam`;
      case 'steam_wishlist':
        return `Wishlist ${cleanInput} on Steam`;
      case 'custom_url':
        return `Visit: ${cleanInput}`;
      default:
        return cleanInput;
    }
  };

  // Helper function to auto-generate URL based on type and input
  const generateUrl = (type, input) => {
    if (!input) return '';
    
    const cleanInput = input.trim().replace(/^@/, ''); // Remove @ prefix if present
    
    switch (type) {
      case 'twitter_follow':
        return `https://x.com/${cleanInput}`;
      case 'instagram_follow':
        return `https://instagram.com/${cleanInput}`;
      case 'youtube_subscribe':
        // YouTube channels can be either @username or channel name
        if (input.trim().startsWith('@')) {
          return `https://youtube.com/${input.trim()}`;
        }
        return `https://youtube.com/@${cleanInput}`;
      case 'twitch_follow':
      case 'twitch_subscribe':
        return `https://twitch.tv/${cleanInput}`;
      case 'kick_follow':
        return `https://kick.com/${cleanInput}`;
      case 'tiktok_follow':
        return `https://tiktok.com/@${cleanInput}`;
      case 'facebook_like':
        return `https://facebook.com/${cleanInput}`;
      case 'reddit_join':
        // Handle if user types 'r/subreddit' or just 'subreddit'
        const subreddit = cleanInput.replace(/^r\//, '');
        return `https://reddit.com/r/${subreddit}`;
      default:
        return '';
    }
  };

  const addRequirement = () => {
    setRequirements([
      ...requirements,
      { type: 'twitter_follow', description: '', url: '', required: true, userInput: '' }
    ]);
  };

  const updateRequirement = (index, field, value) => {
    const updated = [...requirements];
    
    // Sanitize URL field
    if (field === 'url') {
      updated[index][field] = sanitizeUrl(value);
    } else {
      updated[index][field] = value;
    }
    
    // Auto-generate description and URL when userInput changes
    if (field === 'userInput') {
      updated[index].description = generateDescription(updated[index].type, value);
      // Auto-generate URL for follow-type requirements
      const autoUrlTypes = ['twitter_follow', 'instagram_follow', 'youtube_subscribe', 
                           'twitch_follow', 'twitch_subscribe', 'kick_follow', 'tiktok_follow',
                           'facebook_like', 'reddit_join'];
      if (autoUrlTypes.includes(updated[index].type)) {
        updated[index].url = generateUrl(updated[index].type, value);
      }
    }
    
    // Auto-generate description and URL when type changes
    if (field === 'type') {
      updated[index].description = generateDescription(value, updated[index].userInput);
      // Auto-generate URL for follow-type requirements if userInput exists
      const autoUrlTypes = ['twitter_follow', 'instagram_follow', 'youtube_subscribe', 
                           'twitch_follow', 'twitch_subscribe', 'kick_follow', 'tiktok_follow',
                           'facebook_like', 'reddit_join'];
      if (autoUrlTypes.includes(value) && updated[index].userInput) {
        updated[index].url = generateUrl(value, updated[index].userInput);
      }
    }
    
    setRequirements(updated);
  };

  const removeRequirement = (index) => {
    setRequirements(requirements.filter((_, i) => i !== index));
  };

  const handleNumberOfWinnersChange = (value) => {
    const num = parseInt(value) || 1;
    setNumberOfWinners(num);
    
    // Update game keys array to match number of winners
    if (autoPayoutEnabled) {
      const newGameKeys = [];
      for (let i = 0; i < num; i++) {
        newGameKeys.push({
          position: i + 1,
          key: gameKeys[i]?.key || ''
        });
      }
      setGameKeys(newGameKeys);
    }
  };

  const updateGameKey = (position, value) => {
    const updatedKeys = [...gameKeys];
    const index = updatedKeys.findIndex(k => k.position === position);
    if (index !== -1) {
      updatedKeys[index].key = value;
    }
    setGameKeys(updatedKeys);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validation
      if (!formData.title || !formData.description || !formData.prize || !formData.endDate) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      if (new Date(formData.endDate) <= new Date()) {
        setError('End date must be in the future');
        setLoading(false);
        return;
      }

      // Validate scheduled publish date if provided
      if (formData.scheduledPublishDate) {
        const scheduledDate = new Date(formData.scheduledPublishDate);
        const endDate = new Date(formData.endDate);
        const now = new Date();

        if (scheduledDate <= now) {
          setError('Scheduled publish date must be in the future');
          setLoading(false);
          return;
        }

        if (scheduledDate >= endDate) {
          setError('Scheduled publish date must be before the end date');
          setLoading(false);
          return;
        }
      }

      if (requirements.length === 0) {
        setError('Please add at least one requirement');
        setLoading(false);
        return;
      }

      // Validate number of winners
      if (numberOfWinners < 1 || numberOfWinners > 100) {
        setError('Number of winners must be between 1 and 100');
        setLoading(false);
        return;
      }

      // Validate auto-payout fields
      if (autoPayoutEnabled) {
        // Check if we have enough keys
        const validKeys = gameKeys.filter(k => k.key && k.key.trim());
        if (validKeys.length < numberOfWinners) {
          setError(`You must provide ${numberOfWinners} game key${numberOfWinners > 1 ? 's' : ''} for ${numberOfWinners} winner${numberOfWinners > 1 ? 's' : ''}`);
          setLoading(false);
          return;
        }
        
        // Validate each key
        for (let i = 0; i < numberOfWinners; i++) {
          if (!gameKeys[i] || !gameKeys[i].key || !gameKeys[i].key.trim()) {
            setError(`Game key for winner ${i + 1} is required`);
            setLoading(false);
            return;
          }
        }
        
        if (!rerollTimerHours || rerollTimerHours < 1 || rerollTimerHours > 168) {
          setError('Reroll timer must be between 1 and 168 hours');
          setLoading(false);
          return;
        }
      }

      // Validate requirements
      const urlRequiredTypes = [
        'twitter_like', 'twitter_retweet', 'twitter_comment',
        'instagram_like', 'instagram_comment',
        'youtube_like', 'youtube_comment',
        'tiktok_like',
        'facebook_share',
        'reddit_upvote',
        'steam_wishlist',
        'custom_url'
      ];
      
      const autoDescriptionTypes = [
        'twitter_follow', 'instagram_follow', 'youtube_subscribe',
        'twitch_follow', 'twitch_subscribe', 'kick_follow', 'tiktok_follow',
        'facebook_like', 'discord_join', 'reddit_join',
        'steam_follow', 'steam_wishlist', 'custom_url'
      ];
      
      for (let req of requirements) {
        // Check if userInput is required for auto-description types
        if (autoDescriptionTypes.includes(req.type) && !req.userInput?.trim()) {
          setError(`Please fill in the required field for requirement type: ${req.type}`);
          setLoading(false);
          return;
        }
        
        if (!req.description) {
          setError('All requirements must have a description');
          setLoading(false);
          return;
        }
        if (urlRequiredTypes.includes(req.type) && !req.url) {
          setError(`Please provide a URL for "${req.description}"`);
          setLoading(false);
          return;
        }
      }

      // Auto-fix URLs without protocol and clean up requirements
      const fixedRequirements = requirements.map(req => {
        let cleanedReq = { ...req };
        
        // Remove userInput field before sending to backend
        delete cleanedReq.userInput;
        
        // Auto-fix URLs without protocol
        if (cleanedReq.url && !cleanedReq.url.startsWith('http://') && !cleanedReq.url.startsWith('https://')) {
          // Check if it looks like a URL (has a dot)
          if (cleanedReq.url.includes('.')) {
            cleanedReq.url = `https://${cleanedReq.url}`;
          }
        }
        return cleanedReq;
      });

      const token = localStorage.getItem('token');
      const data = new FormData();
      
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('prize', formData.prize);
      data.append('endDate', formData.endDate);
      
      // Add scheduled publish date if provided
      if (formData.scheduledPublishDate) {
        data.append('scheduledPublishDate', formData.scheduledPublishDate);
      }
      
      data.append('requirements', JSON.stringify(fixedRequirements));

      if (formData.specialThanksName || formData.specialThanksMessage) {
        data.append('specialThanks', JSON.stringify({
          name: formData.specialThanksName,
          message: formData.specialThanksMessage
        }));
      }

      if (banner) {
        data.append('banner', banner);
      }

      data.append('numberOfWinners', numberOfWinners.toString());

      if (autoPayoutEnabled) {
        data.append('autoPayoutEnabled', 'true');
        data.append('gameKeys', JSON.stringify(gameKeys.slice(0, numberOfWinners)));
        data.append('gameKey', gameKeys[0]?.key || ''); // Legacy field
        data.append('rerollTimerHours', rerollTimerHours.toString());
      }

      if (autoRollEnabled) {
        data.append('autoRollEnabled', 'true');
      }

      if (supporterBonus) {
        data.append('supporterBonus', 'true');
      }

      const response = await axios.post('/api/giveaways', data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      onSuccess(response.data.giveaway);
    } catch (err) {
      console.error('Create giveaway error:', err);
      setError(err.response?.data?.message || 'Failed to create giveaway');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal-overlay"
    >
      <div
        className="modal-content"
      >
        <h2 className="modal-title">🎁 Create a Giveaway</h2>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Template Section */}
          <div className="form-group" style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            padding: '20px', 
            borderRadius: '12px',
            marginBottom: '25px',
            border: '2px solid #667eea'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.5rem' }}>📋</span>
                <label className="form-label" style={{ marginBottom: 0, color: 'white' }}>
                  Giveaway Templates
                </label>
              </div>
              <button
                type="button"
                onClick={() => setShowTemplateSection(!showTemplateSection)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                {showTemplateSection ? 'Hide' : 'Show'}
              </button>
            </div>

            {showTemplateSection && (
              <>
                <select
                  value={selectedTemplate?._id || ''}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    fontSize: '1rem',
                    marginBottom: '15px'
                  }}
                  disabled={loadingTemplates}
                >
                  <option value="">-- Select a template --</option>
                  {templates.map(template => (
                    <option key={template._id} value={template._id}>
                      {template.name} ({template.usageCount} uses)
                    </option>
                  ))}
                </select>

                <div style={{ 
                  background: 'rgba(255,255,255,0.1)', 
                  padding: '15px', 
                  borderRadius: '8px',
                  marginTop: '15px'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'white', marginBottom: '10px' }}>
                    <input
                      type="checkbox"
                      checked={saveAsTemplate}
                      onChange={(e) => setSaveAsTemplate(e.target.checked)}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span style={{ fontSize: '1rem', fontWeight: '500' }}>Save this giveaway as a template</span>
                  </label>
                  
                  {saveAsTemplate && (
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <input
                        type="text"
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        placeholder="Template name..."
                        style={{
                          flex: 1,
                          padding: '10px',
                          borderRadius: '6px',
                          border: '1px solid rgba(255,255,255,0.3)',
                          background: 'rgba(255,255,255,0.1)',
                          color: 'white'
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleSaveAsTemplate}
                        style={{
                          background: 'rgba(255,255,255,0.2)',
                          border: '1px solid rgba(255,255,255,0.3)',
                          padding: '10px 20px',
                          borderRadius: '6px',
                          color: 'white',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        Save Template
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Scheduled Publishing Section */}
          <div className="form-group" style={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
            padding: '20px', 
            borderRadius: '12px',
            marginBottom: '25px',
            border: '2px solid #f5576c'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
              <span style={{ fontSize: '1.5rem' }}>📅</span>
              <label className="form-label" style={{ marginBottom: 0, color: 'white' }}>
                Scheduled Publishing (Optional)
              </label>
            </div>
            <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)', marginBottom: '15px' }}>
              Set a future date and time for this giveaway to automatically go live. Leave empty to publish immediately.
            </p>
            <input
              type="datetime-local"
              name="scheduledPublishDate"
              value={formData.scheduledPublishDate}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.3)',
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Title */}
          <div className="form-group">
            <label className="form-label">
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              maxLength={200}
              placeholder="e.g., Steam Deck Giveaway"
              className="form-input"
              required
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              maxLength={2000}
              placeholder="Describe your giveaway..."
              rows={4}
              className="form-textarea"
              required
            />
          </div>

          {/* Banner */}
          <div className="form-group">
            <label className="form-label">
              Banner Image (Optional)
            </label>
            {bannerPreview && (
              <div className="banner-preview">
                <img
                  src={bannerPreview}
                  alt="Banner preview"
                />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleBannerChange}
              className="file-input"
            />
            <p className="form-hint">
              Max size: 10MB. Recommended: 1200x630px
            </p>
          </div>

          {/* Prize */}
          <div className="form-group">
            <label className="form-label">
              Prize *
            </label>
            <input
              type="text"
              name="prize"
              value={formData.prize}
              onChange={handleInputChange}
              maxLength={500}
              placeholder="e.g., Steam Deck 512GB + $50 Steam Gift Card"
              className="form-input"
              required
            />
          </div>

          {/* End Date */}
          <div className="form-group">
            <label className="form-label">
              End Date & Time *
            </label>
            <input
              type="datetime-local"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              className="form-input"
              required
            />
          </div>

          {/* Number of Winners */}
          <div className="form-group">
            <label className="form-label">
              Number of Winners * 🏆
            </label>
            <input
              type="number"
              value={numberOfWinners}
              onChange={(e) => handleNumberOfWinnersChange(e.target.value)}
              min="1"
              max="100"
              placeholder="1"
              className="form-input"
              required
            />
            <p className="form-hint">
              How many winners will be selected (1-100)
            </p>
          </div>

          {/* Special Thanks */}
          <div className="special-thanks-section">
            <h4>💝 Special Thanks (Optional)</h4>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.9rem' }}>
                Name
              </label>
              <input
                type="text"
                name="specialThanksName"
                value={formData.specialThanksName}
                onChange={handleInputChange}
                placeholder="Person or organization to thank"
                className="form-input"
                style={{ fontSize: '0.95rem' }}
              />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: '0.9rem' }}>
                Message
              </label>
              <textarea
                name="specialThanksMessage"
                value={formData.specialThanksMessage}
                onChange={handleInputChange}
                placeholder="Your thank you message..."
                rows={3}
                className="form-textarea"
                style={{ fontSize: '0.95rem' }}
              />
            </div>
          </div>

          {/* Requirements */}
          <div className="form-group">
            <div className="requirements-header">
              <h4>📋 Entry Requirements *</h4>
              <button
                type="button"
                onClick={addRequirement}
                className="add-requirement-btn"
              >
                + Add Requirement
              </button>
            </div>

            {requirements.map((req, index) => (
              <div
                key={index}
                className="requirement-card"
              >
                <div className="requirement-header">
                  <label className="requirement-label">
                    Requirement #{index + 1}
                  </label>
                  <button
                    type="button"
                    onClick={() => removeRequirement(index)}
                    className="remove-requirement-btn"
                  >
                    Remove
                  </button>
                </div>

                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>Type</label>
                  <select
                    value={req.type}
                    onChange={(e) => updateRequirement(index, 'type', e.target.value)}
                    className="form-select"
                    style={{ fontSize: '0.95rem' }}
                  >
                    {requirementTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Input field based on requirement type */}
                {['twitter_follow', 'instagram_follow', 'youtube_subscribe', 'twitch_follow', 'twitch_subscribe', 
                  'kick_follow', 'tiktok_follow', 'facebook_like', 'discord_join', 'reddit_join', 
                  'steam_follow', 'steam_wishlist', 'custom_url'].includes(req.type) ? (
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
                      {req.type === 'twitter_follow' && 'Twitter Username/Handle *'}
                      {req.type === 'instagram_follow' && 'Instagram Username/Handle *'}
                      {req.type === 'youtube_subscribe' && 'YouTube Channel Name *'}
                      {req.type === 'twitch_follow' && 'Twitch Username *'}
                      {req.type === 'twitch_subscribe' && 'Twitch Channel Name *'}
                      {req.type === 'kick_follow' && 'Kick Username *'}
                      {req.type === 'tiktok_follow' && 'TikTok Username/Handle *'}
                      {req.type === 'facebook_like' && 'Facebook Page Name *'}
                      {req.type === 'discord_join' && 'Discord Server Name *'}
                      {req.type === 'reddit_join' && 'Subreddit Name *'}
                      {req.type === 'steam_follow' && 'Steam Profile/Game Name *'}
                      {req.type === 'steam_wishlist' && 'Game Name *'}
                      {req.type === 'custom_url' && 'URL Description *'}
                    </label>
                    <input
                      type="text"
                      value={req.userInput || ''}
                      onChange={(e) => updateRequirement(index, 'userInput', e.target.value)}
                      placeholder={
                        req.type === 'twitter_follow' ? '@GamersUnion or GamersUnion' :
                        req.type === 'instagram_follow' ? '@gamersunion or gamersunion' :
                        req.type === 'youtube_subscribe' ? 'Channel name' :
                        req.type === 'twitch_follow' || req.type === 'twitch_subscribe' ? 'username' :
                        req.type === 'kick_follow' ? 'username' :
                        req.type === 'tiktok_follow' ? '@username or username' :
                        req.type === 'facebook_like' ? 'Page name' :
                        req.type === 'discord_join' ? 'Server name' :
                        req.type === 'reddit_join' ? 'gaming or r/gaming' :
                        req.type === 'steam_follow' ? 'Profile/Game name' :
                        req.type === 'steam_wishlist' ? 'Game name' :
                        req.type === 'custom_url' ? 'Brief description' :
                        'Enter details'
                      }
                      className="form-input"
                      style={{ fontSize: '0.95rem' }}
                    />
                    {req.description && (
                      <div style={{ 
                        marginTop: '5px', 
                        padding: '8px', 
                        backgroundColor: '#f0f0f0', 
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        color: '#555'
                      }}>
                        Preview: <strong>{req.description}</strong>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
                      Description *
                    </label>
                    <input
                      type="text"
                      value={req.description || ''}
                      onChange={(e) => updateRequirement(index, 'description', e.target.value)}
                      placeholder="e.g., Like this post on Twitter"
                      className="form-input"
                      style={{ fontSize: '0.95rem' }}
                    />
                  </div>
                )}

                {/* URL Required Types */}
                {['twitter_like', 'twitter_retweet', 'twitter_comment', 'instagram_like', 'instagram_comment', 
                  'youtube_like', 'youtube_comment', 'tiktok_like', 'facebook_share', 'reddit_upvote', 
                  'steam_wishlist', 'custom_url'].includes(req.type) && (
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
                      URL * (Required for this type)
                    </label>
                    <input
                      type="text"
                      value={req.url || ''}
                      onChange={(e) => updateRequirement(index, 'url', e.target.value)}
                      placeholder="twitter.com/username/status/123 (protocol auto-added)"
                      className="form-input"
                      style={{ fontSize: '0.95rem' }}
                    />
                    <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '3px', marginBottom: '0' }}>
                      💡 Just paste the URL - we'll automatically format it correctly
                    </p>
                  </div>
                )}

                {/* URL Optional Types */}
                {!['twitter_like', 'twitter_retweet', 'twitter_comment', 'instagram_like', 'instagram_comment',
                    'youtube_like', 'youtube_comment', 'tiktok_like', 'facebook_share', 'reddit_upvote',
                    'steam_wishlist', 'custom_url'].includes(req.type) && (
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
                      URL (Optional - e.g., link to your profile)
                    </label>
                    <input
                      type="text"
                      value={req.url || ''}
                      onChange={(e) => updateRequirement(index, 'url', e.target.value)}
                      placeholder="twitter.com/username (protocol auto-added)"
                      className="form-input"
                      style={{ fontSize: '0.95rem' }}
                    />
                    <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '3px', marginBottom: '0' }}>
                      💡 Just paste the URL - we'll automatically format it correctly
                    </p>
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={req.required}
                    onChange={(e) => updateRequirement(index, 'required', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  <label style={{ fontSize: '0.85rem' }}>Required</label>
                </div>
              </div>
            ))}

            {requirements.length === 0 && (
              <p className="requirements-empty">
                No requirements added yet. Click "Add Requirement" to get started.
              </p>
            )}
          </div>

          {/* Auto-Payout Section */}
          <div className="special-thanks-section" style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            padding: '20px', 
            borderRadius: '12px',
            border: '2px solid #764ba2'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
              <input
                type="checkbox"
                id="autoPayoutToggle"
                checked={autoPayoutEnabled}
                onChange={(e) => setAutoPayoutEnabled(e.target.checked)}
                style={{ 
                  marginRight: '10px', 
                  width: '20px', 
                  height: '20px',
                  cursor: 'pointer'
                }}
              />
              <label htmlFor="autoPayoutToggle" style={{ 
                fontSize: '1.1rem', 
                fontWeight: 'bold', 
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🤖 Enable Auto-Payout
              </label>
            </div>

            <p style={{ 
              fontSize: '0.85rem', 
              color: '#e0e0e0', 
              marginBottom: '15px',
              lineHeight: '1.5'
            }}>
              Automatically distribute game keys when winners claim them via DM. Keys are sent by KeyDistributor bot and will auto-reroll if not claimed within your specified time limit.
            </p>

            {autoPayoutEnabled && (
              <>
                <div className="form-group">
                  <label className="form-label" style={{ color: 'white', fontSize: '0.9rem', marginBottom: '10px' }}>
                    Game Keys * 🔐
                  </label>
                  <small style={{ color: '#ffd700', fontSize: '0.75rem', display: 'block', marginBottom: '15px' }}>
                    ⚠️ These keys will be encrypted and stored securely. Provide one key for each winner.
                  </small>
                  
                  {gameKeys.slice(0, numberOfWinners).map((keyData, index) => (
                    <div key={index} style={{ marginBottom: '15px' }}>
                      <label style={{ color: 'white', fontSize: '0.85rem', display: 'block', marginBottom: '5px' }}>
                        {numberOfWinners > 1 ? `Winner #${index + 1} Key` : 'Game Key'}
                      </label>
                      <input
                        type="text"
                        value={keyData.key}
                        onChange={(e) => updateGameKey(index + 1, e.target.value)}
                        placeholder="XXXXX-XXXXX-XXXXX"
                        className="form-input"
                        style={{ fontSize: '0.95rem', fontFamily: 'monospace' }}
                        required={autoPayoutEnabled}
                      />
                    </div>
                  ))}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ color: 'white', fontSize: '0.9rem' }}>
                    Reroll Timer (Hours) * ⏱️
                  </label>
                  <input
                    type="number"
                    value={rerollTimerHours}
                    onChange={(e) => setRerollTimerHours(parseInt(e.target.value))}
                    placeholder="24"
                    min="1"
                    max="168"
                    className="form-input"
                    style={{ fontSize: '0.95rem' }}
                    required={autoPayoutEnabled}
                  />
                  <small style={{ color: '#e0e0e0', fontSize: '0.75rem' }}>
                    How long each winner has to claim before auto-reroll (1-168 hours)
                  </small>
                </div>
              </>
            )}
          </div>

          {/* Auto-Roll Section */}
          <div className="special-thanks-section" style={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
            padding: '20px', 
            borderRadius: '12px',
            border: '2px solid #f5576c'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
              <input
                type="checkbox"
                id="autoRollToggle"
                checked={autoRollEnabled}
                onChange={(e) => setAutoRollEnabled(e.target.checked)}
                style={{ 
                  marginRight: '10px', 
                  width: '20px', 
                  height: '20px',
                  cursor: 'pointer'
                }}
              />
              <label htmlFor="autoRollToggle" style={{ 
                fontSize: '1.1rem', 
                fontWeight: 'bold', 
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🎲 Enable Auto-Roll
              </label>
            </div>

            <p style={{ 
              fontSize: '0.85rem', 
              color: '#fff', 
              marginBottom: '0',
              lineHeight: '1.5'
            }}>
              Automatically roll for winners when the giveaway ends. No manual intervention required - the system will select winners immediately after the end date.
            </p>
          </div>

          {/* Supporter Bonus Section */}
          <div className="special-thanks-section" style={{ 
            background: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)', 
            padding: '20px', 
            borderRadius: '12px',
            border: '2px solid #19547b'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
              <input
                type="checkbox"
                id="supporterBonusToggle"
                checked={supporterBonus}
                onChange={(e) => setSupporterBonus(e.target.checked)}
                style={{ 
                  marginRight: '10px', 
                  width: '20px', 
                  height: '20px',
                  cursor: 'pointer'
                }}
              />
              <label htmlFor="supporterBonusToggle" style={{ 
                fontSize: '1.1rem', 
                fontWeight: 'bold', 
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🎁 Enable Supporter Bonus
              </label>
            </div>

            <p style={{ 
              fontSize: '0.85rem', 
              color: '#fff', 
              marginBottom: '0',
              lineHeight: '1.5'
            }}>
              Award +100 points to everyone who enters this giveaway! This is a great way to thank your supporters and encourage participation.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="submit-btn"
            >
              {loading ? 'Creating...' : 'Create Giveaway'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGiveaway;
